import { useState, useMemo } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import type {
  UpcomingFixturesProps,
  MatchdayFixtures,
  Fixture,
} from '../types'

// =============================================================================
// Helpers
// =============================================================================

function formatKickoffTime(isoString: string): { time: string; period: string } {
  const date = new Date(isoString)
  const hours = date.getHours()
  const minutes = date.getMinutes()
  const period = hours >= 12 ? 'PM' : 'AM'
  const displayHours = hours % 12 || 12
  const displayMinutes = minutes === 0 ? '' : `:${minutes.toString().padStart(2, '0')}`
  return { time: `${displayHours}${displayMinutes}`, period }
}

function formatCountdown(isoString: string): string | null {
  const now = new Date()
  const kickoff = new Date(isoString)
  const diff = kickoff.getTime() - now.getTime()

  if (diff <= 0) return null

  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}

function formatDateHeader(isoString: string): string {
  const date = new Date(isoString)
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  })
}

function groupFixturesByDate(fixtures: Fixture[]): { date: string; fixtures: Fixture[] }[] {
  const groups: Record<string, Fixture[]> = {}
  const order: string[] = []

  for (const fixture of fixtures) {
    const dateKey = new Date(fixture.kickoff).toDateString()
    if (!groups[dateKey]) {
      groups[dateKey] = []
      order.push(dateKey)
    }
    groups[dateKey].push(fixture)
  }

  return order.map(dateKey => ({
    date: formatDateHeader(groups[dateKey][0].kickoff),
    fixtures: groups[dateKey],
  }))
}

// =============================================================================
// Sub-components
// =============================================================================

function MatchdaySelector({ matchdays, currentIndex, onChange }: {
  matchdays: MatchdayFixtures[]
  currentIndex: number
  onChange: (index: number) => void
}) {
  const canGoPrev = currentIndex > 0
  const canGoNext = currentIndex < matchdays.length - 1
  const current = matchdays[currentIndex]

  return (
    <div className="flex items-center justify-between px-4 py-3
                    sm:px-6">
      <button
        onClick={() => canGoPrev && onChange(currentIndex - 1)}
        disabled={!canGoPrev}
        className={`flex items-center justify-center w-9 h-9 rounded-full border transition-colors
                    ${canGoPrev
                      ? 'border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 cursor-pointer'
                      : 'border-zinc-100 dark:border-zinc-800 text-zinc-300 dark:text-zinc-600 cursor-default'}`}
      >
        <ChevronLeft className="w-5 h-5" />
      </button>

      <div className="flex items-center gap-1.5">
        <span className="text-base font-bold text-zinc-900 dark:text-zinc-100">
          Round {current.matchday}
        </span>
      </div>

      <button
        onClick={() => canGoNext && onChange(currentIndex + 1)}
        disabled={!canGoNext}
        className={`flex items-center justify-center w-9 h-9 rounded-full border transition-colors
                    ${canGoNext
                      ? 'border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 cursor-pointer'
                      : 'border-zinc-100 dark:border-zinc-800 text-zinc-300 dark:text-zinc-600 cursor-default'}`}
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  )
}

function DateHeader({ date }: { date: string }) {
  return (
    <div className="px-4 py-2
                    sm:px-6">
      <div className="flex items-center justify-center px-3 py-1.5 rounded-full
                      bg-zinc-100 dark:bg-zinc-800">
        <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
          {date}
        </span>
      </div>
    </div>
  )
}

function TeamLogo({ logo, name }: { logo: string; name: string }) {
  const isUrl = logo.startsWith('http://') || logo.startsWith('https://') || logo.startsWith('/')

  if (!isUrl) {
    // Fallback to initials
    const initials = name.slice(0, 2).toUpperCase()
    return (
      <div className="w-8 h-8 rounded-full bg-zinc-100 dark:bg-zinc-800 overflow-hidden shrink-0
                      flex items-center justify-center">
        <span className="text-xs font-bold text-zinc-500 dark:text-zinc-400">
          {initials}
        </span>
      </div>
    )
  }

  return (
    <div className="w-8 h-8 rounded-full bg-zinc-100 dark:bg-zinc-800 overflow-hidden shrink-0
                    flex items-center justify-center">
      <img
        src={logo}
        alt={name}
        className="w-6 h-6 object-contain"
        onError={(e) => {
          const target = e.target as HTMLImageElement
          target.style.display = 'none'
        }}
      />
    </div>
  )
}

function FixtureRow({ fixture, onSelect }: { fixture: Fixture; onSelect?: () => void }) {
  const { time, period } = formatKickoffTime(fixture.kickoff)
  const countdown = formatCountdown(fixture.kickoff)

  return (
    <button
      type="button"
      onClick={() => onSelect?.()}
      className="w-full group cursor-pointer"
    >
      <div className="px-4 py-3 transition-colors
                      hover:bg-zinc-50 dark:hover:bg-zinc-800/50
                      sm:px-6">
        {/* Venue + countdown row */}
        <div className="flex items-center justify-center gap-2 mb-2">
          <span className="text-[10px] text-zinc-400 dark:text-zinc-500 truncate">
            {fixture.venue}
          </span>
          {countdown && (
            <>
              <span className="text-zinc-300 dark:text-zinc-600 text-[10px]">&middot;</span>
              <span className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400 shrink-0">
                {countdown}
              </span>
            </>
          )}
        </div>

        {/* Teams + time row */}
        <div className="flex items-center">
          {/* Home team — right aligned */}
          <div className="flex-1 flex items-center justify-end gap-2.5 min-w-0">
            <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100 text-right truncate">
              {fixture.homeTeam.shortName}
            </span>
            <TeamLogo logo={fixture.homeTeam.logo} name={fixture.homeTeam.shortName} />
          </div>

          {/* Kickoff time — centered */}
          <div className="flex flex-col items-center justify-center w-16 shrink-0 mx-2">
            <span className="text-base font-bold tabular-nums text-zinc-900 dark:text-zinc-100 leading-none">
              {time}
            </span>
            <span className="text-[10px] font-semibold uppercase text-zinc-400 dark:text-zinc-500 mt-0.5">
              {period}
            </span>
          </div>

          {/* Away team — left aligned */}
          <div className="flex-1 flex items-center gap-2.5 min-w-0">
            <TeamLogo logo={fixture.awayTeam.logo} name={fixture.awayTeam.shortName} />
            <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">
              {fixture.awayTeam.shortName}
            </span>
          </div>
        </div>
      </div>
    </button>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export function UpcomingFixtures({
  matchdays,
  initialMatchday,
  onFixtureSelect,
  onMatchdayChange,
}: UpcomingFixturesProps) {
  const initialIndex = useMemo(() => {
    if (initialMatchday) {
      const idx = matchdays.findIndex(m => m.matchday === initialMatchday)
      return idx >= 0 ? idx : 0
    }
    return 0
  }, [matchdays, initialMatchday])

  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const currentMatchday = matchdays[currentIndex]

  const dateGroups = useMemo(
    () => currentMatchday ? groupFixturesByDate(currentMatchday.fixtures) : [],
    [currentMatchday]
  )

  function handleMatchdayChange(index: number) {
    setCurrentIndex(index)
    onMatchdayChange?.(matchdays[index].matchday)
  }

  if (!matchdays.length || !currentMatchday) {
    return (
      <div className="max-w-2xl mx-auto py-6">
        <div className="mb-5">
          <h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
            Upcoming Fixtures
          </h1>
        </div>
        <div className="text-center py-12 text-zinc-500 dark:text-zinc-400">
          <p>No upcoming fixtures available.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto py-6
                    sm:px-6
                    md:px-0">
      {/* Page header */}
      <div className="mb-5">
        <h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
          Upcoming Fixtures
        </h1>
      </div>

      {/* Matchday selector */}
      <MatchdaySelector
        matchdays={matchdays}
        currentIndex={currentIndex}
        onChange={handleMatchdayChange}
      />

      {/* Fixture groups by date */}
      <div className="mt-2">
        {dateGroups.map((group) => (
          <div key={group.date}>
            <DateHeader date={group.date} />
            <div className="divide-y divide-dotted divide-zinc-200 dark:divide-zinc-800">
              {group.fixtures.map((fixture) => (
                <FixtureRow
                  key={fixture.id}
                  fixture={fixture}
                  onSelect={() => onFixtureSelect?.(fixture.id)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
